import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Warehouse pages reuse the same large item/category/meta payloads. A short-lived
// in-memory cache prevents route transitions and sibling components from issuing
// duplicate HTTP requests for identical data. Any warehouse write or auth-session
// change invalidates the cache so responses can never be reused across users.
const WAREHOUSE_READ_TTL_MS = 10000;
const warehouseReadCache = new Map();
const warehouseReadInflight = new Map();
const CACHEABLE_WAREHOUSE_READS = new Set([
  "/warehouse/items",
  "/warehouse/categories",
  "/warehouse/meta",
]);

const normalizeWarehouseReadUrl = (url) => {
  const value = String(url || "");
  const path = value.split("?")[0].replace(/\/+$/, "");
  return CACHEABLE_WAREHOUSE_READS.has(path) ? path : null;
};

const stableParamsKey = (params) => {
  if (!params || typeof params !== "object") return "";
  try {
    return JSON.stringify(Object.keys(params).sort().reduce((out, key) => {
      out[key] = params[key];
      return out;
    }, {}));
  } catch {
    return "";
  }
};

export const clearWarehouseReadCache = () => {
  warehouseReadCache.clear();
  warehouseReadInflight.clear();
};

const rawGet = api.get.bind(api);
api.get = (url, config = {}) => {
  const cacheablePath = normalizeWarehouseReadUrl(url);
  if (!cacheablePath || config?.warehouseCache === false) return rawGet(url, config);

  const key = `${cacheablePath}|${stableParamsKey(config?.params)}`;
  const now = Date.now();
  const cached = warehouseReadCache.get(key);
  if (cached && cached.expiresAt > now) return Promise.resolve(cached.response);
  if (cached) warehouseReadCache.delete(key);

  const pending = warehouseReadInflight.get(key);
  if (pending) return pending;

  const request = rawGet(url, config)
    .then((response) => {
      warehouseReadCache.set(key, {
        response,
        expiresAt: Date.now() + WAREHOUSE_READ_TTL_MS,
      });
      return response;
    })
    .finally(() => warehouseReadInflight.delete(key));

  warehouseReadInflight.set(key, request);
  return request;
};

api.interceptors.response.use(
  (response) => {
    try {
      const method = String(response?.config?.method || "get").toLowerCase();
      const url = String(response?.config?.url || "");
      if (method !== "get" && url.includes("/warehouse/")) clearWarehouseReadCache();
    } catch {}
    return response;
  },
  (error) => {
    if (error?.response?.status === 401) clearWarehouseReadCache();
    return Promise.reject(error);
  }
);


export class ApiContractError extends Error {
  constructor(message, { url = "", expected = "", received = null } = {}) {
    super(message);
    this.name = "ApiContractError";
    this.url = url;
    this.expected = expected;
    this.received = received;
  }
}

const contractError = (response, expected) => {
  const url = String(response?.config?.url || "API");
  const received = response?.data;
  const error = new ApiContractError(
    `Sunucudan beklenmeyen veri alındı (${url}). Sayfayı yenileyip tekrar deneyin.`,
    { url, expected, received },
  );
  console.error("[API contract]", { url, expected, received });
  return error;
};

export const requireObjectData = (response, label = "nesne") => {
  const value = response?.data;
  if (!value || typeof value !== "object" || Array.isArray(value)) throw contractError(response, label);
  return value;
};

export const requireItemsData = (response) => {
  const value = requireObjectData(response, "{ items: [] }");
  if (!Array.isArray(value.items)) throw contractError(response, "{ items: [] }");
  return value;
};

export const apiErrorMessage = (error, fallback = "Bir hata oluştu. Lütfen tekrar deneyin.") => {
  if (error instanceof ApiContractError) return error.message;
  const detail = error?.response?.data?.detail;
  if (detail != null) return formatError(detail);
  if (!error?.response && typeof error?.message === "string" && error.message.trim()) {
    return `Sunucuya ulaşılamadı: ${error.message}`;
  }
  return fallback;
};

export function formatError(detail) {
  if (detail == null) return "Bir hata oluştu. Lütfen tekrar deneyin.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => {
      if (!e || typeof e !== "object") return String(e || "");
      const location = Array.isArray(e.loc) ? e.loc.filter((part) => part !== "body").join(" → ") : "";
      const message = typeof e.msg === "string" ? e.msg : JSON.stringify(e);
      return location ? `${location}: ${message}` : message;
    }).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
